import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class LogisticService {
    constructor(private datatask: DataTaskService) {}

    /**
     * Retrieves a list of equipments.
     *
     * @datatask_keyno 30
     * @param params the settings for the equipment list call.
     * @returns a list over equipments according to the given params.
     */
    public getEquipments(params?: Partial<EquipmentParams>): Promise<Item[]> {
        return this.datatask.Post(30, params);
    }

    /**
     * Retrieves list over product groups.
     *
     * @datatask_keyno 41
     * @param add_all whether an option for selecting all product group should be included in the list, default `'0'` / `false`.
     * @returns a promise containing list over product groups.
     */
    public getProductGroups(add_all?: '1' | '0'): Promise<ProductGroupItem[]> {
        return this.datatask.Post(41, { add_all: add_all });
    }

    /**
     * Retrieves list of businesses and / or companies.
     *
     * @datatask_keyno 104
     * @param add_all whether an option for selecting all businesscos should be included in the list, default `'0'` / `false`.
     * @returns list of businesses and / or companies.
     */
    public getCompanies(add_all?: '1' | '0'): Promise<BusinesscoItem[]> {
        return this.datatask.Post(104, { add_all: add_all });
    }

    /**
     * Retrieves list of departments.
     *
     * @datatask_keyno 105
     * @param businessco_no departments under the given businessco_no, if not provided retrieves all departments.
     * @returns a promise containing a list of departments.
     */
    public getDepartments(businessco_no?: number | string): Promise<DepartmentItem[]> {
        return this.datatask.Post(105, { businessco_no: businessco_no });
    }

    /**
     * Retrieves list of products sizes of the product with the given prod id.
     *
     * @datatask_keyno 135
     * @param prod_id the id of the product to retrieve product sizes for.
     * @returns a promise containing a list of product sizes.
     */
    public getProductSizes(prod_id: string | number): Promise<Item[]> {
        return this.datatask.Post(135, { prod_id: prod_id });
    }

    /**
     * Retrieves list of completionmethods.
     *
     * @datatask_keyno 216
     * @param add_all whether an option for selecting all completion method should be included in the list, default `'0'` / `false`.
     * @returns a promise containing list of completion methods.
     */
    public getPohCompletionMethods(add_all?: '1' | '0'): Promise<PohCompletionMethodItem[]> {
        return this.datatask.Post(216, { add_all: add_all });
    }

    /**
     * Retrieves list of completion principles.
     *
     * @datatask_keyno 217
     * @param add_all whether an option for selecting all completion principles should be included in the list, default `'0'` / `false`.
     * @returns a promise containing list of completion principles.
     */
    public getPohCompletionPrinciples(add_all?: '1' | '0'): Promise<PohCompletionPrincipleItem[]> {
        return this.datatask.Post(217, { add_all: add_all });
    }

    /**
     * Retrieves list of unit types.
     *
     * @datatask_keyno 218
     * @param params the settings for the unit list call.
     * @returns a promise containing a list of units according to the settings.
     */
    public getUnitList(params?: Partial<UnitListParams>): Promise<UnitItem[]> {
        return this.datatask.Post(218, params);
    }

    /**
     * Retrieves list of countries.
     *
     * @datatask_keyno 220
     * @param add_all whether an option for selecting all countries should be included in the list, default `'0'` / `false`.
     * @returns a promise containing list of countries.
     */
    public getCountries(add_all?: '1' | '0'): Promise<CountryItem[]> {
        return this.datatask.Post(220, { add_all: add_all });
    }

    /**
     * Returns list of municipalities for the given country.
     *
     * @datatask 2210
     * @datataskName sp_ws_p2_comunity_list
     * @param params country id to get municipalities for.
     * @returns promise containing list of municipalities.
     */
    public getMunicipalities(params: GetMunicipalitiesParams): Promise<MunicipalityItem[]> {
        return this.datatask.Post(2210, params);
    }

    /**
     * Retrieves list of operations.
     *
     * @datatask 265
     * @datataskName sp_ws_p2_operation_list
     * @param params the settings for the operation list call.
     * @returns a promise containing a list of operations.
     */
    public getOperations(params: OperationListParams): Promise<OperationItem[]> {
        return this.datatask.Post(265, params);
    }

    /**
     * Retrieves list of printers.
     *
     * @datatask_keyno 337
     * @returns a promise containing list of printers.
     */
    public getPrinters(): Promise<Item[]> {
        return this.datatask.Post(337);
    }

    /**
     * Retrieves list over product choices.
     *
     * @datatask_keyno 366
     * @param params the settings for the product choice list call.
     * @returns a promise containing a list of product choices.
     */
    public getProductChoices(params: ProductChoiceListParams) {
        return this.datatask.Post(366, params);
    }

    /**
     * Retrieves a list of stockplaces for the given business-co, on the given businessco-site.
     *
     * @datatask_keyno 622
     * @param params the settings for the stockplaces call.
     * @returns a promise containing a list of stockplaces.
     */
    public getStockplaces(params?: Partial<StockplaceParams>): Promise<StockplaceItem[]> {
        return this.datatask.Post(622, { businessco_no: params?.businessco_no, businesssite_keyno: params?.businesssite_keyno, add_all: params?.add_all || '' });
    }

    /**
     * Retrieves list of stock locations at the stock place with the given stockplace id.
     *
     * @param stockplaceId the id of the stockplace to retreieve list of stocklocation for.
     * @returns a promise containing the list of stocklocations.
     */
    public getStockLocations(stockplaceId: string): Promise<StockLocation[]> {
        return this.datatask.Post(1051, { stockplace_id: stockplaceId });
    }

    /**
     * Retrieves list of trolleys at the stockplace with the stockplace id in the params.
     *
     * @param params the settings for the trolleys call.
     * @returns promise containing list of trolleys.
     */
    public getTrolleys(params: TrolleysParams): Promise<Item[]> {
        return this.datatask.Post(1058, params);
    }

    /**
     * Retrieves list of delivery terms.
     *
     * @datatask 623
     * @datataskName sp_ws_p2_deliveryterm_list
     * @returns promise containing list of delivery terms.
     */
    public getDeliveryTerms(): Promise<DeliveryTermItem[]> {
        return this.datatask.Post(623);
    }

    /**
     * Retrieves list of delivery methods.
     *
     * @datatask 385
     * @datataskName sp_ws_p2_deliverymethod_list
     * @param params
     * @returns promise containing list of delivery methods.
     */
    public getDeliveryMethods(params?: Partial<DeliveryMethodsParams>): Promise<DeliveryMethodItem[]> {
        return this.datatask.Post(385, params);
    }

    /**
     * Retrieves list of portal suers that are purchasers.
     *
     * @datatask 684
     * @datataskName sp_ws_p2_portal_user_list_purch
     * @returns promise containing list of portal users that are purchasers.
     */
    public getPurchaserPortalUsers(params?: Partial<PurchaserPortalUsersParams>): Promise<Item[]> {
        return this.datatask.Post(684, params);
    }

    /**
     * Retrieves list of busineeses.
     *
     * @datatask 638
     * @datataskName sp_ws_p2_business_list
     * @returns promise containing list of businesses.
     */
    public getBusinesses(): Promise<BusinessItem[]> {
        return this.datatask.Post(638);
    }

    /**
     * Retrieves list of coops.
     *
     * @datatask 639
     * @datataskName sp_ws_p2_coop_list
     * @param params
     * @returns promise containing list of coops.
     */
    public async getCoops(params?: GetCoopsParams): Promise<CoopItem[]> {
        return this.datatask.Post(639, params);
    }

    /**
     * Retrieves list of ordertypes.
     *
     * @datatask 612
     * @datataskName sp_ws_p2_ordertype_list
     * @param params settings for the list.
     * @returns promise containing list of ordertypes.
     */
    public getOrderTypes(params?: GetOrderTypesParams): Promise<OrderTypeItem[]> {
        return this.datatask.Post(612, params);
    }

    /**
     * Retrieves list of orderstatuses.
     *
     * @datatask 620
     * @datataskName sp_ws_p2_orderstatus_list
     * @param params settings for the list.
     * @returns promise containing list of orderstatuses.
     */
    public getOrderStatuses(params?: GetOrderStatusesParams): Promise<OrderStatusItem[]> {
        return this.datatask.Post(620, params);
    }

    /**
     * Retrieves list of orderstatuses.
     *
     * @datatask 679
     * @datataskName sp_ws_p2_prodplace_list
     * @param params settings for the list.
     * @returns promise containing list of production places.
     */
    public getProductionPlaces(params?: GetProductionPlaceParams): Promise<ProductionPlaceItem[]> {
        return this.datatask.Post(679, params);
    }

    /**
     * Retrieves list of refill methods.
     *
     * @datatask 997
     * @datataskName sp_ws_p2_prodbeomethod_list
     * @param params settings for the list.
     * @returns promise containing list of refill methods.
     */
    public getProdBeoMethods(params?: GetProdBeoMethodsParams): Promise<ProdBeoMethodItem[]> {
        return this.datatask.Post(997, params);
    }

    /**
     * @datatask 494
     * @datataskName sp_ws_p2_planpolicy_list
     * @returns promise containing list of plan policies.
     */
    public getPlanPolicies(): Promise<PlanPolicyItem[]> {
        return this.datatask.Post(494);
    }

    /**
     * @datatask 960
     * @datataskName sp_ws_p2_productcycle_list_ext
     * @returns promise containing list of product cycles.
     */
    public getProductCycles(): Promise<ProductCycleItem[]> {
        return this.datatask.Post(960);
    }
}

export interface ProductCycleItem {
    prodcycle_no: string;
    prodcycle_name: string;
    orderby: string;
}

export interface PlanPolicyItem {
    planpolicy_no: string;
    planpolicy_name: string;
}

export interface GetOrderStatusesParams {
    add_all?: '1' | '0';
    add_include_active?: '1' | '0';
}

export interface GetProductionPlaceParams {}

export interface GetProdBeoMethodsParams {}

export interface OrderStatusItem {
    orderstatus_id: string;
    orderstatus_name: string;
    orderby: string;
}

export interface ProductionPlaceItem {
    prodplace_id: string;
    prodplace_name: string;
    orderby: string;
}

export interface ProdBeoMethodItem {
    prodbeomethod_no: string;
    prodbeomethod_name: string;
    orderby: string;
}

export interface GetOrderTypesParams {
    webpage_name?: string;
    add_all?: '1' | '0';
}

export interface OrderTypeItem {
    ordertype_id: string;
    ordertype_name: string;
    orderby: string;
}

export interface CoopItem {
    coop_id: string;
    coop_name: string;
    orderby: `${number}`;
}

export interface GetCoopsParams {
    /**
     * @default '0'
     */
    add_all?: '1' | '0';
}

export interface BusinessItem {
    business_id: string;
    business_name: string;
    ordarby: `${number}`;
}

export interface PurchaserPortalUsersParams {
    businessco_no: string | number;

    /**
     * @default '0'
     */
    add_all: '1' | '0';
}

export interface DeliveryMethodsParams {
    /**
     * Filters by delivery methods for the given businessco.
     */
    businessco_no: string | number;

    /**
     * Filters by the businessco for the order.
     */
    order_internal_no: string | number;
}

/**
 * Represents parameters used for retrieving list of equipments.
 */
export interface EquipmentParams {
    /**
     * The operation to retrieve equipments from, if any.
     */
    operation_id: string | number;

    /**
     * Adds the equipments last used by the labour with the labour_no to the top of the list, if any.
     */
    labour_no: string | number;

    /**
     * Filters the equipments to the business of the given businessco_no, if any. If empty retrieves all list across all businesses.
     */
    businessco_no: string | number;
}

/**
 * Represents a product group list item.
 */
export interface ProductGroupItem {
    /**
     * The id of the product group.
     */
    prodgrp_id: string;

    /**
     * The name of the product group.
     */
    prodgrp_name: string;

    /**
     * The order place in the list of this product group.
     */
    orderby: string;
}

/**
 * Represents parameters used for retrieving list of stockplaces.
 */
export interface StockplaceParams {
    /**
     * The businessco to filter the stockplaces list with, if undefined all stockplaces are retrieved.
     */
    businessco_no?: string | number;

    /**
     * The businessco site to filter the stockplaces list with, if undefined all stockplaces across all sites are retrieved.
     */
    businesssite_keyno?: string;

    /**
     * Whether an item for viewing all stockplaces should be included in the stockplaces list.
     */
    add_all?: 'ALL' | '1' | '0' | '';
}

/**
 * Represents a stockplace item.
 */
export interface StockplaceItem extends Item {
    /**
     * The id of the stockplace.
     */
    stockplace_id: string;

    /**
     * The displayed name of the stockplace.
     */
    stockplace_name: string;

    /**
     * the order place of this stockplace item.
     */
    orderby: string;
}

/**
 * Represents a business or company item.
 */
export interface BusinesscoItem {
    /**
     * The id of the businessco.
     */
    businessco_no: string;

    /**
     * The name of the businessco.
     */
    company_name: string;

    /**
     * The order place of this businessco.
     */
    orderby: string;
}

/**
 * Represents a department item.
 */
export interface DepartmentItem {
    /**
     * The id of the department item.
     */
    department_id: string;

    /**
     * The name of the deparment.
     */
    department_name: string;

    /**
     * The order place of the department itme in the list.
     */
    orderby: string;
}

/**
 * Represents a poh completion method item.
 */
export interface PohCompletionMethodItem {
    /**
     * The id of the poh completion method.
     */
    pohcompletionmethod_keyno: string;

    /**
     * The displayed name of the completion method.
     */
    pohcompletionmethod_name: string;

    /**
     * The order place of the completion method in the list.
     */
    orderby: string;
}

export interface PohCompletionPrincipleItem {
    /**
     * The id of completion principle.
     */
    pohcompletionprinciple_keyno: string;

    /**
     * The displayed named of the completion principle.
     */
    pohcompletionprinciple_name: string;
    /**
     * The order place of the completion principle in the list.
     */
    orderby: string;
}

/**
 * Represents parameters used for retrieving list of units.
 */
export interface UnitListParams {
    /**
     * Whether the unit item is active or not, `'-'` indicates both.
     */
    isactive: '1' | '0' | '-';

    /**
     * Whether to add an option for selecting all units in the list, default `'0'` / `false`.
     */
    add_all: '1' | '0';

    /**
     * Whether to only retrieve time-related units, default `'0'` / `false`.
     */
    onlytimeunits: '1' | '0';

    /**
     * Whether to display the full unit name in the list, default `'0'` / `false`.
     */
    fullname: '1' | '0';
}

/**
 * Represents a unit as list item.
 */
export interface UnitItem {
    /**
     * The id of the unit item.
     */
    unit_no: string;

    /**
     * The name of the unit item.
     */
    unit_name: string;

    /**
     * The order place of the unit item in the list.
     */
    orderby: string;
}

export interface CountryItem {
    /**
     * The id of this country item.
     */
    country_id: string;

    /**
     * The displayed name of this country item.
     */
    country_name: string;

    /**
     * The order place of the country item in the list.
     */
    orderby: string;
}

export interface GetMunicipalitiesParams {
    country_id: string;
}

export interface MunicipalityItem {
    comunity_id: string;
    comunity_name: string;
    orderby: `${number}`;
}

export interface OperationItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_parms: string;
    orderby: string;
    prod_id: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
}

/**
 * Represents parameters used for retrieving list of operations.
 */
export interface OperationListParams {
    /**
     * Whether to filter by direct operation or not.
     */
    direct: '1' | '0';

    /**
     * Equipment id to filter operations on, if any.
     */
    equipment_id?: string;

    /**
     * Work group to filter operations on, if any.
     */
    workgrp_keyno?: string | number;

    /**
     * Whether to only retrieve active operations, or all.
     * @default '1' / true
     */
    show_only_active?: '1' | '0';

    /**
     * Factory to filter the operations on.
     */
    factory_id?: string;
}

/**
 * Represents parameters used for retrieving product-choice list.
 */
export interface ProductChoiceListParams {
    /**
     * The prod id of the product to retrieve product choices of.
     */
    prod_id: string;

    /**
     * Uses the current businessco of the user if not defined.
     */
    businessco_no?: string | number;

    /**
     * Retrieves from businessco if not defined.
     */
    stockplace_id?: string | number;

    orderline_keyno?: string | number;

    /**
     * Whether to only retrieve product choices which are in stock.
     */
    onlyonstock?: '1' | '0';
}
/**
 * Represents a stock location at a stockplace.
 */
export interface StockLocation {
    /**
     * The id of the stock location.
     */
    stocklocation_keyno: string;

    /**
     * The name of the stock location.
     */
    stocklocation_name: string;

    /**
     * The sort order for this stock location in the list.
     */
    orderby: string;
}

/**
 * Represents parameters used to configure request for list of trolleys.
 */
export interface TrolleysParams {
    /**
     * The id of the stockplace to retrieve list of trolleys for.
     */
    stockplace_id: string;

    /**
     * Whether an option for selecting all trolleys should be added to the list.
     */
    addall?: '1' | '0';
}

export interface DeliveryMethodItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_path: string;
    item_parms: string;
    orderby: string;
    item_filtervalue: string;
}

export interface DeliveryTermItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_path: string;
    item_parms: string;
    orderby: string;
}
// listDeliveryMethods: function (parms) {
//     return $ihttp.post({
//         method: 385,
//         parameters: parms || {}
//     });
// },
// listCargonizerPrinters: function (parms) {
//     return $ihttp.post({
//         method: 423,
//         parameters: parms || {}
//     });
// },
// listStockOnLocations: function (parms) {
//     return $ihttp.post({
//         method: 459,
//         parameters: parms || {}
//     });
// },
// listStockLocation: function (parms) { // stockzoneclass_keyno, factory_id
//     return $ihttp.post({
//         method: 472,
//         parameters: parms || {}
//     });
// },
// listPlanPolicies: function (parms) {
//     return $ihttp.post({
//         method: 494,
//         parameters: parms || {}
//     });
// },
// //loadOrderStatusList: function (orderstatus_id) {
//     //return p2DataTaskService.call(620, {
//         //orderstatus_id: orderstatus_id
//     //});
// //},

// //loadOrderTypes: function (ordertype_id) {
//     //return p2DataTaskService.call(612, {
//         //ordertype_id: ordertype_id
//     //});
// //},
// listOrderStatuses: function (parms) {
//     return $ihttp.post({
//         method: 620,
//         parameters: parms || {}
//     });
// },
// listOrderTypes: function (parms) {
//     return $ihttp.post({
//         method: 612,
//         parameters: parms || {}
//     });
// },

// // Used in orderhead_copy
// //loadOrderTypeList: function (ordertype_id) {
// //    return p2DataTaskService.call(612, {
// //        ordertype_id: ordertype_id
// //    });
// //},

// // If you find this procedure named logisticService.loadStockPlaceList() in any controller/component, you should rename it to logisticService.listStockPlaces(). - ODL
// loadStockPlaceList: function (businessco_no, add_all) {
//     return p2DataTaskService.call(622, {
//         businessco_no: businessco_no,
//         add_all: add_all || ''
//     });
// },

// listStockPlaces: function (businessco_no, add_all) {
//     return p2DataTaskService.call(622, {
//         businessco_no: businessco_no,
//         add_all: add_all || ''
//     });
// },

// // Use this procedure name instead of loadStockPlaceList. - ODL
// listStocks: function (parms) { // businessco_no, add_all || {}
//     return $ihttp.post({
//         method: 622,
//         parameters: parms || {}
//     });
// },
// listDeliveryTerms: function (parms) {
//     return $ihttp.post({
//         method: 623,
//         parameters: parms || {}
//     });
// },
// listBusinesses: function (parms) {
//     return $ihttp.post({
//         method: 638,
//         parameters: parms || {}
//     });
// },
// listCoops: function (parms) {
//     return $ihttp.post({
//         method: 639,
//         parameters: parms || {}
//     });
// },
// listSuppliers: function (parms) {
//     return $ihttp.post({
//         method: 653,
//         parameters: parms || {}
//     });
// },
// listProductPlaces: function (parms) {
//     return $ihttp.post({
//         method: 679,
//         parameters: parms || {}
//     });
// },
// listProductionPlaces: function (parms) {
//     return $ihttp.post({
//         method: 679,
//         parameters: parms || {}
//     });
// },
// listPortalUserPurchases: function (parms) {
//     return $ihttp.post({
//         method: 684,
//         parameters: parms || {}
//     });
// },
// getNextCounterNumber: function (parms) {
//     return $ihttp.post({
//         method: 678,
//         parameters: parms || {}
//     });
// },
// getStockPlace: function (parms) {
//     return $ihttp.post({
//         method: 738,
//         parameters: parms || {}
//     });
// },
// loadProdSizeColliList: function (prodsize_keyno) {
//     return p2DataTaskService.call(913, {
//         prodsize_keyno: prodsize_keyno
//     });
// },
// listStockZones: function (parms) {
//     return $ihttp.post({
//         method: 929,
//         parameters: parms || {}
//     });
// },
// listProductCycles: function (parms) {
//     return $ihttp.post({
//         method: 960,
//         parameters: parms || {}
//     });
// },
// listProdBeoMethods: function (parms) {
//     return $ihttp.post({
//         method: 997,
//         parameters: parms || {}
//     });
// },
// listProductionOrderMethods: function (parms) {
//     return $ihttp.post({
//         method: 997,
//         parameters: parms || {}
//     });
// },
// listStockLocations: function (parms) {
//     return $ihttp.post({
//         method: 1051,
//         parameters: parms || {}
//     });
// },
// listTrolleys: function (parms) { // stockplace_id
//     return $ihttp.post({
//         method: 1058,
//         parameters: parms || {}
//     });
// },
// listStockCorrectionCodes: function (parms) {
//     return $ihttp.post({
//         method: 1071,
//         parameters: parms || {}
//     });
// },
// listCounties: function (parms) {
//     return $ihttp.post({
//         method: 1307,
//         parameters: parms || {}
//     });
// }
